import type { SxProps, Theme } from '@mui/material'

/**
 * Per-block styling stored under `config.style`. All values are constrained to
 * fixed, safe choices (no arbitrary CSS) so styling can be applied without any
 * injection surface, and the same tokens render identically in the email
 * renderer (services/blocks.py).
 */
export interface BlockStyle {
  background?: BackgroundKey
  align?: 'left' | 'center' | 'right'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  width?: 'contained' | 'full'
  marginTop?: 'none' | 'sm' | 'md' | 'lg'
  marginBottom?: 'none' | 'sm' | 'md' | 'lg'
}

export type BackgroundKey =
  | 'none'
  | 'subtle'
  | 'primary'
  | 'accent'
  | 'dark'
  | 'light'

// Fixed background palette. `bg` is a CSS color; `fg` is the readable text color
// applied on top. Kept in sync with the email renderer's palette.
export const BACKGROUNDS: Record<BackgroundKey, { label: string; bg: string; fg?: string }> = {
  none: { label: 'None', bg: 'transparent' },
  subtle: { label: 'Subtle', bg: 'rgba(0,117,188,0.06)' },
  light: { label: 'Light gray', bg: '#F4F6F8' },
  primary: { label: 'Primary', bg: '#0075BC', fg: '#ffffff' },
  accent: { label: 'Accent', bg: '#1C5580', fg: '#ffffff' },
  dark: { label: 'Dark', bg: '#283138', fg: '#ffffff' },
}

const PAD: Record<NonNullable<BlockStyle['padding']>, number> = {
  none: 0,
  sm: 1.5,
  md: 3,
  lg: 5,
}

const GAP: Record<NonNullable<BlockStyle['marginTop']>, number> = {
  none: 0,
  sm: 1,
  md: 2.5,
  lg: 5,
}

export function getBlockStyle(config: Record<string, unknown> | undefined): BlockStyle {
  const s = (config?.style as BlockStyle) ?? {}
  return s ?? {}
}

/** Build the MUI sx for a block's style config (site rendering). */
export function blockStyleSx(config: Record<string, unknown> | undefined): SxProps<Theme> {
  const s = getBlockStyle(config)
  const bg = s.background && s.background !== 'none' ? BACKGROUNDS[s.background] : undefined
  const pad = s.padding ? PAD[s.padding] : bg ? PAD.md : 0
  const sx: Record<string, unknown> = {}
  if (bg) {
    sx.bgcolor = bg.bg
    if (bg.fg) sx.color = bg.fg
    sx.borderRadius = 3
  }
  if (pad) sx.p = pad
  if (s.align) sx.textAlign = s.align
  if (s.marginTop && s.marginTop !== 'none') sx.mt = GAP[s.marginTop]
  if (s.marginBottom && s.marginBottom !== 'none') sx.mb = GAP[s.marginBottom]
  // Contained width centers the block with a max width; full is edge-to-edge.
  if (s.width === 'contained') {
    sx.maxWidth = 720
    sx.mx = 'auto'
  }
  return sx as SxProps<Theme>
}
